/**
 * @fileoverview Labeled select field molecule.
 * @description Composes the Label and Select atoms with an optional error message, mirroring
 * FormField's text-input treatment so a form mixing text fields and dropdowns looks consistent.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Label from '@/shared/components/atoms/Label'
import Select from '@/shared/components/atoms/Select'

interface SelectFieldProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  id: string
  label: string
  options: { value: string; label: string; disabled?: boolean | undefined }[]
  error?: string | undefined
  'data-testid'?: string
}

/**
 * @component
 * @param {SelectFieldProps} props - Standard select attributes plus id/label/options/error.
 * @param {string} props.id - Also used to associate the label with the select.
 * @param {string} props.label - The visible field label.
 * @param {{ value: string; label: string; disabled?: boolean }[]} props.options - The dropdown's
 * options; a disabled one is shown but cannot be chosen.
 * @param {string} [props.error] - Validation message shown below the field, if any.
 * @param {string} [props['data-testid']] - QA locator for the select. The error message gets the
 * same id with a trailing "-select" swapped for "-error".
 */
function SelectField({
  id,
  label,
  options,
  error,
  'data-testid': testId,
  ...selectProps
}: SelectFieldProps) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-2">
        <Select
          id={id}
          options={options}
          invalid={Boolean(error)}
          aria-invalid={Boolean(error)}
          data-testid={testId}
          {...selectProps}
        />
      </div>
      {error && (
        <p
          className="mt-1.5 text-[13px] text-red-600"
          data-testid={testId ? `${testId.replace(/-select$/, '')}-error` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default SelectField
